/**
 * Provides a centralized administration interface for managing widget-specific
 * configurations across the application. Enables administrators to discover,
 * search, view, and modify dashboard widget settings through dynamically
 * rendered configuration panels, while enforcing edit permissions and
 * supporting a consistent configuration management experience.
 *
 * The component serves as a container for widget administration features,
 * including dashboard layout management, integrations, reporting URLs,
 * visibility controls, timezone settings, Quick Links, Key Guidelines,
 * and other widget-specific configuration modules. It also supports
 * search-based filtering, expandable configuration sections, and
 * role-based edit capabilities for streamlined administration.
 */
import React, { useMemo, useState } from "react";

import AllProgramsTabConfig from "./AllProgramsTabConfig";
import AnalyticsConfig from "./AnalyticsConfig";
import DashboardConfig from "./DashboardConfig";
import DisbursementConfig from "./DisbursementConfig";
import FinancialsConfig from "./FinancialsConfig";
import IdeaTrackerConfig from "./IdeaTrackerConfig";
import KeyGuidelinesConfig from "./KeyGuidelinesConfig";
import MeetingsConfig from "./MeetingsConfig";
import NotificationWidgetConfig from "./NotificationWidgetConfig";
import OversightConfig from "./OversightConfig";
import PartnerDirectoryConfig from "./PartnerDirectoryConfig";
import QuickLinksConfig from "./QuickLinksConfig";
import TaskPrioritiesConfig from "./TaskPrioritiesConfig";
import TimezoneConfig from "./TimezoneConfig";

interface WidgetItem {
  itemDisplayName: string;
  itemComponentName: string;
  isEditable: boolean;
  displayOrder: number;
}

interface Props {
  items: WidgetItem[];
}

const componentMap: Record<string, React.ComponentType<any>> = {
  QuickLinksConfig,
  DashboardConfig,
  TimezoneConfig,
  AllProgramsTabConfig,
  KeyGuidelinesConfig,
  NotificationWidgetConfig,
  TaskPrioritiesConfig,
  OversightConfig,
  FinancialsConfig,
  AnalyticsConfig,
  DisbursementConfig,
  PartnerDirectoryConfig,
  MeetingsConfig,
  IdeaTrackerConfig,
};

const WidgetConfiguration: React.FC<Props> = ({ items }) => {
  const [search, setSearch] = useState("");
  const [expandedWidget, setExpandedWidget] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return items.filter((item) =>
      item.itemDisplayName
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [items, search]);

  return (
    <div className="bg-[#f6f4ef] min-h-screen font-sans">
      <div className="p-5">
        <div className="bg-white border border-gray-200 rounded-md">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center">
            <div className="flex-1">
              <h2 className="text-md font-semibold text-gray-800">
                Widget Configuration
              </h2>
            </div>
          </div>

          <div className="p-5">
            <input
              type="text"
              placeholder="Search widgets..."
              className="w-full border border-gray-200 p-2 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-orange-400"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className="space-y-3 max-h-[600px] overflow-auto">
              {filtered.map((item) => {
                const isExpanded =
                  expandedWidget === item.itemDisplayName;

                const Component =
                  componentMap[item.itemComponentName];

                return (
                  <div
                    key={item.itemComponentName}
                    className="border border-gray-200 rounded-md bg-white"
                  >
                    <div className="p-4 flex justify-between items-center">
                      <div className="text-sm font-medium text-gray-800">
                        {item.itemDisplayName}
                      </div>

                      <button
                        className={`px-3 py-1.5 rounded-md text-sm ${
                          item.isEditable
                            ? "bg-[var(--color-brand)] text-white"
                            : "bg-gray-200 text-gray-500 cursor-not-allowed"
                        }`}
                        disabled={!item.isEditable}
                        onClick={() =>
                          item.isEditable &&
                          setExpandedWidget(
                            isExpanded
                              ? null
                              : item.itemDisplayName
                          )
                        }
                      >
                        {isExpanded ? "Close" : "Edit"}
                      </button>
                    </div>

                    {isExpanded && Component && (
                      <div className="border-t border-gray-200 p-4 bg-[#fbfaf7]">
                        {item.itemComponentName ===
                        "QuickLinksConfig" ? (
                          <QuickLinksConfig
                            widget={item.itemDisplayName}
                            onBack={() => {}}
                          />
                        ) : (
                          <Component />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {filtered.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-6">
                  No widgets found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetConfiguration;